/**
 * The Admin's WhatsApp numbers of the Central (D636, doc 25 phase 1, owner Q-365: Meta's Cloud API, direct) - framework-neutral view-models.
 *
 *  - **Operações › Números de WhatsApp** (`central_whatsapp`): the business numbers the Central talks to customers through - each with the
 *    COUNTRY (and optionally one city) it serves, its status (waiting / active / paused / retired), the WhatsApp quality rating, the approved
 *    templates the desk may send outside the 24 h window and the words that make a customer opt out. The three secrets of a number (access
 *    token, app secret, webhook verify token) are WRITE-ONLY: the screen sets them and shows only a configured badge and a masked hint.
 *
 * The console only presents: the server authorizes every call at the number's country (and city) and validates every write. The checks below
 * run the SAME kernel (`@vello/whatsapp-adapter`) as the server, so a draft is refused before the round trip with the SAME stable reason code,
 * which the screen turns into a sentence through `problem.central_whatsapp`. Statuses, qualities, categories and credential kinds are codes shown
 * through the vocabulary catalog (`central_whatsapp.*`); neutral: no country, city, language or number format is named here.
 */
import type { CentralWhatsappCredentialsView, CentralWhatsappNumberPatch, CentralWhatsappNumberView, CentralWhatsappRegisterInput, CentralWhatsappTemplateView, OperatingMarketView } from '@vello/api-client';
import {
  DEFAULT_GRAPH_API_VERSION, DEFAULT_OPT_OUT_KEYWORDS, NUMBER_CREDENTIAL_KINDS, NUMBER_STATUSES, QUALITY_RATINGS, TEMPLATE_CATEGORIES,
  editBusinessNumber, normalizeKeyword, placeholderCount, registerBusinessNumber, validateTemplate,
  type BusinessNumber, type NumberStatus,
} from '@vello/whatsapp-adapter';
import { grantsAllow, type KnownGrants } from './console-access.js';

/** What the operator may do with WhatsApp numbers (the server re-authorizes every call). */
export function centralWhatsappAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayManage: boolean } {
  if (grants === undefined) return { mayView: false, mayManage: false };
  return {
    mayView: grantsAllow(grants, { permission: 'whatsapp:view', target: 'GEO' }),
    mayManage: grantsAllow(grants, { permission: 'whatsapp:manage', target: 'GEO' }),
  };
}

export const NUMBER_STATUS_CHOICES: readonly string[] = NUMBER_STATUSES;
export const NUMBER_QUALITY_CHOICES: readonly string[] = QUALITY_RATINGS;
export const TEMPLATE_CATEGORY_CHOICES: readonly string[] = TEMPLATE_CATEGORIES;
export const CREDENTIAL_KIND_ORDER: readonly string[] = NUMBER_CREDENTIAL_KINDS;

export type NumberTone = 'neutral' | 'success' | 'warning' | 'danger';
export const numberStatusTone = (status: string): NumberTone => (status === 'ACTIVE' ? 'success' : status === 'PENDING' ? 'warning' : 'neutral');
// The ratings come from the kernel's list (healthy, throttled, blocked) so no colour word is written here.
const [RATING_HEALTHY, RATING_THROTTLED, RATING_BLOCKED] = QUALITY_RATINGS;
export const qualityTone = (rating: string): NumberTone => (rating === RATING_HEALTHY ? 'success' : rating === RATING_THROTTLED ? 'warning' : rating === RATING_BLOCKED ? 'danger' : 'neutral');

/** The name shown for a number: the operator-written label when there is one, else the number a customer sees. */
export const numberDisplayName = (n: Pick<CentralWhatsappNumberView, 'label' | 'displayNumber'>): string => (n.label !== undefined && n.label !== '' ? n.label : n.displayNumber);

// ---- credentials -------------------------------------------------------------------------------------------------

/** All three secrets are set: the only state a number can be activated from. */
export const credentialsComplete = (c: CentralWhatsappCredentialsView): boolean => CREDENTIAL_KIND_ORDER.every((k) => c[k as keyof CentralWhatsappCredentialsView]?.configured === true);

/** The kinds still missing, in screen order. */
export const missingCredentials = (c: CentralWhatsappCredentialsView): readonly string[] => CREDENTIAL_KIND_ORDER.filter((k) => c[k as keyof CentralWhatsappCredentialsView]?.configured !== true);

// ---- status actions ----------------------------------------------------------------------------------------------

export interface StatusAction {
  readonly to: 'ACTIVE' | 'PAUSED' | 'RETIRED';
  /** `activate` and `resume` both go to ACTIVE; the screen words them differently. */
  readonly kind: 'activate' | 'resume' | 'pause' | 'retire';
  /** Retiring frees the Meta number and cannot be undone: the screen asks first. */
  readonly confirm: boolean;
}

/** The status moves open from a number's current status (mirrors the kernel's lifecycle), in screen order. */
export function statusActions(status: string): readonly StatusAction[] {
  switch (status as NumberStatus) {
    case 'PENDING': return [{ to: 'ACTIVE', kind: 'activate', confirm: false }, { to: 'RETIRED', kind: 'retire', confirm: true }];
    case 'ACTIVE': return [{ to: 'PAUSED', kind: 'pause', confirm: false }, { to: 'RETIRED', kind: 'retire', confirm: true }];
    case 'PAUSED': return [{ to: 'ACTIVE', kind: 'resume', confirm: false }, { to: 'RETIRED', kind: 'retire', confirm: true }];
    default: return [];
  }
}

// ---- the editor draft --------------------------------------------------------------------------------------------

export interface TemplateDraft {
  readonly name: string;
  readonly language: string;
  readonly category: string;
  readonly preview: string;
}

export interface NumberDraft {
  readonly phoneNumberId: string;
  readonly displayNumber: string;
  readonly wabaId: string;
  readonly countryCode: string;
  /** '' = the whole country. */
  readonly marketId: string;
  readonly label: string;
  readonly apiVersion: string;
  readonly qualityRating: string;
  /** Comma- or space-separated words as typed. */
  readonly keywordsText: string;
  readonly templates: readonly TemplateDraft[];
}

export function newNumberDraft(countryCode = ''): NumberDraft {
  return { phoneNumberId: '', displayNumber: '', wabaId: '', countryCode, marketId: '', label: '', apiVersion: DEFAULT_GRAPH_API_VERSION, qualityRating: RATING_HEALTHY, keywordsText: DEFAULT_OPT_OUT_KEYWORDS.join(', '), templates: [] };
}

export function draftFromNumber(n: CentralWhatsappNumberView): NumberDraft {
  return {
    phoneNumberId: n.phoneNumberId, displayNumber: n.displayNumber, wabaId: n.wabaId, countryCode: n.countryCode, marketId: n.marketId ?? '', label: n.label ?? '', apiVersion: n.apiVersion,
    qualityRating: n.qualityRating, keywordsText: n.optOutKeywords.join(', '),
    templates: n.templates.map((t) => ({ name: t.name, language: t.language, category: t.category, preview: t.preview })),
  };
}

export const emptyTemplate = (): TemplateDraft => ({ name: '', language: '', category: 'utility', preview: '' });
export const replaceTemplate = (list: readonly TemplateDraft[], index: number, next: TemplateDraft): readonly TemplateDraft[] => list.map((t, i) => (i === index ? next : t));
export const removeTemplate = (list: readonly TemplateDraft[], index: number): readonly TemplateDraft[] => list.filter((_, i) => i !== index);

/** The words typed in a field: split on commas, semicolons and spaces, normalised, each once. */
export function parseKeywords(text: string): readonly string[] {
  const out: string[] = [];
  for (const piece of text.split(/[\s,;]+/)) {
    const k = normalizeKeyword(piece);
    if (k !== '' && !out.includes(k)) out.push(k);
  }
  return out;
}

/** A template draft as the wire shape: the parameter count is the highest placeholder of the preview (the server checks they agree). */
export const templateOf = (t: TemplateDraft): CentralWhatsappTemplateView => ({ name: t.name.trim(), language: t.language.trim(), category: t.category, preview: t.preview.trim(), paramCount: placeholderCount(t.preview) });

/** How many `{{n}}` parameters a template draft's text takes (shown beside the text). */
export const templateParamCount = (t: Pick<TemplateDraft, 'preview'>): number => placeholderCount(t.preview);

/**
 * The first stable reason code that refuses a NEW number (the kernel's own checks, the same codes the server answers with), or undefined.
 * `index` names the template at fault when that is the issue.
 */
export function createIssue(draft: NumberDraft): { readonly code: string; readonly templateIndex?: number } | undefined {
  const made = registerBusinessNumber({
    numberId: 'draft', phoneNumberId: draft.phoneNumberId.trim(), displayNumber: draft.displayNumber.trim(), wabaId: draft.wabaId.trim(), countryCode: draft.countryCode,
    ...(draft.marketId !== '' ? { marketId: draft.marketId } : {}), ...(draft.label.trim() !== '' ? { label: draft.label.trim() } : {}), apiVersion: draft.apiVersion.trim(),
    templates: draft.templates.map(templateOf), optOutKeywords: parseKeywords(draft.keywordsText), createdBy: 'draft',
  }, '1970-01-01T00:00:00.000Z');
  if (made.ok) return undefined;
  return withTemplateIndex(made.error.code, draft);
}

function withTemplateIndex(code: string, draft: NumberDraft): { readonly code: string; readonly templateIndex?: number } {
  if (code !== 'WA_NUMBER_TEMPLATE_INVALID') return { code };
  const at = draft.templates.findIndex((t, i) => !validateTemplate(templateOf(t)).ok || draft.templates.findIndex((o) => o.name.trim() === t.name.trim() && o.language.trim() === t.language.trim()) !== i);
  return at >= 0 ? { code, templateIndex: at } : { code };
}

const asBusinessNumber = (n: CentralWhatsappNumberView): BusinessNumber => ({
  numberId: n.numberId, phoneNumberId: n.phoneNumberId, displayNumber: n.displayNumber, wabaId: n.wabaId, countryCode: n.countryCode, ...(n.marketId !== undefined ? { marketId: n.marketId } : {}),
  ...(n.label !== undefined ? { label: n.label } : {}), status: n.status as NumberStatus, apiVersion: n.apiVersion, qualityRating: n.qualityRating as BusinessNumber['qualityRating'], templates: n.templates as BusinessNumber['templates'],
  optOutKeywords: n.optOutKeywords, version: n.version, createdAt: n.createdAt, createdBy: n.createdBy, updatedAt: n.updatedAt, updatedBy: n.updatedBy,
});

/** The patch that turns `number` into the draft: only the fields that really differ. */
export function numberDraftPatch(number: CentralWhatsappNumberView, draft: NumberDraft): CentralWhatsappNumberPatch {
  const patch: { -readonly [K in keyof CentralWhatsappNumberPatch]: CentralWhatsappNumberPatch[K] } = {};
  if (draft.displayNumber.trim() !== number.displayNumber) patch.displayNumber = draft.displayNumber.trim();
  if (draft.wabaId.trim() !== number.wabaId) patch.wabaId = draft.wabaId.trim();
  if (draft.marketId !== (number.marketId ?? '')) patch.marketId = draft.marketId === '' ? null : draft.marketId;
  if (draft.label.trim() !== (number.label ?? '')) patch.label = draft.label.trim() === '' ? null : draft.label.trim();
  if (draft.apiVersion.trim() !== number.apiVersion) patch.apiVersion = draft.apiVersion.trim();
  if (draft.qualityRating !== number.qualityRating) patch.qualityRating = draft.qualityRating;
  const templates = draft.templates.map(templateOf);
  if (JSON.stringify(templates) !== JSON.stringify(number.templates)) patch.templates = templates;
  const keywords = parseKeywords(draft.keywordsText);
  if (JSON.stringify(keywords) !== JSON.stringify(number.optOutKeywords)) patch.optOutKeywords = keywords;
  return patch;
}

export const numberHasChanges = (patch: CentralWhatsappNumberPatch): boolean => Object.keys(patch).length > 0;

/** The first stable reason code that refuses editing `number` into the draft (the kernel's own checks), or undefined (also when nothing changed). */
export function editIssue(number: CentralWhatsappNumberView, draft: NumberDraft): { readonly code: string; readonly templateIndex?: number } | undefined {
  const patch = numberDraftPatch(number, draft);
  if (!numberHasChanges(patch)) return undefined;
  const edited = editBusinessNumber(asBusinessNumber(number), patch, 'draft', '1970-01-01T00:00:00.000Z');
  return edited.ok ? undefined : withTemplateIndex(edited.error.code, draft);
}

/** The registration payload for a draft (the server stamps the creator). */
export function registerInput(draft: NumberDraft): CentralWhatsappRegisterInput {
  return {
    phoneNumberId: draft.phoneNumberId.trim(), displayNumber: draft.displayNumber.trim(), wabaId: draft.wabaId.trim(), countryCode: draft.countryCode,
    ...(draft.marketId !== '' ? { marketId: draft.marketId } : {}), ...(draft.label.trim() !== '' ? { label: draft.label.trim() } : {}), apiVersion: draft.apiVersion.trim(),
    templates: draft.templates.map(templateOf), optOutKeywords: parseKeywords(draft.keywordsText),
  };
}

// ---- lists --------------------------------------------------------------------------------------------------------

/** The countries to offer in a filter or a new number: those of the markets the operator can see plus those of the loaded numbers, sorted. */
export function numberCountries(markets: readonly OperatingMarketView[], numbers: readonly Pick<CentralWhatsappNumberView, 'countryCode'>[]): readonly string[] {
  return [...new Set([...markets.filter((m) => m.status !== 'RETIRED').map((m) => m.countryCode.toUpperCase()), ...numbers.map((n) => n.countryCode.toUpperCase())])].sort();
}

/** The registered, non-retired markets of a country (the choices of a number's city). */
export const numberMarketsOfCountry = (markets: readonly OperatingMarketView[], countryCode: string): readonly OperatingMarketView[] =>
  markets.filter((m) => m.countryCode.toUpperCase() === countryCode.toUpperCase() && m.status !== 'RETIRED');

/** The callback URL to enter in the Meta console for a number: the API's origin plus the number's webhook path. */
export function callbackUrl(apiBaseUrl: string, webhookPath: string): string {
  return `${apiBaseUrl.replace(/\/+$/, '')}${webhookPath}`;
}
